import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetDynamicsDealConfigQuery,
	useGetDynamicsOwnersQuery,
	useGetDynamicsPipelinesQuery,
	useGetDynamicsSourceFieldsQuery,
	useSaveDynamicsDealConfigMutation,
} from "@/pages/Private/redux/dynamics/dynamics.api";

import { UpdateMsdynamicsDealConfig } from "../schema/dynamics";
import { DynamicsPipelinePicker } from "./DynamicsPipelinePicker";
import { resolveSelectableStageId } from "./dynamicsPipeline.utils";

interface DealConfigDraft {
	defaultPipelineId: string | null;
	defaultStageId: string | null;
	dealNameTemplate: string;
	expectedCloseDateOffset: number;
	defaultOwnerId: string | null;
}

const EMPTY_DRAFT: DealConfigDraft = {
	defaultPipelineId: null,
	defaultStageId: null,
	dealNameTemplate: "",
	expectedCloseDateOffset: 30,
	defaultOwnerId: null,
};

/**
 * `PUT deal-config` patches with `@IsOptional() @IsString()` fields, so an unset value has
 * to be **omitted** — sending `null` (as the Pipedrive tab does) fails validation.
 */
const buildPayload = (draft: DealConfigDraft): UpdateMsdynamicsDealConfig => {
	const payload: UpdateMsdynamicsDealConfig = {
		expectedCloseDateOffset: draft.expectedCloseDateOffset,
	};

	if (draft.defaultPipelineId) {
		payload.defaultPipelineId = draft.defaultPipelineId;
	}

	if (draft.defaultStageId) {
		payload.defaultStageId = draft.defaultStageId;
	}

	if (draft.dealNameTemplate.trim()) {
		payload.dealNameTemplate = draft.dealNameTemplate.trim();
	}

	if (draft.defaultOwnerId) {
		payload.defaultOwnerId = draft.defaultOwnerId;
	}

	return payload;
};

/**
 * Opportunity defaults.
 *
 * Deliberately renders **no currency input**: Dataverse takes an opportunity's currency from
 * `transactioncurrencyid`, a lookup, so it cannot be a configuration string — it would need a
 * picker over the environment's `transactioncurrency` records, which is a backend schema
 * change, not a field to add here.
 */
export const DynamicsDealConfigTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.msdynamics.dealConfig.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: dealConfig, isLoading: isLoadingConfig } = useGetDynamicsDealConfigQuery();
	const { data: pipelinesData } = useGetDynamicsPipelinesQuery();
	const { data: ownersData } = useGetDynamicsOwnersQuery();
	const { data: sourceFields } = useGetDynamicsSourceFieldsQuery();
	const [saveDealConfig, { isLoading: isSaving }] = useSaveDynamicsDealConfigMutation();

	const [draft, setDraft] = useState<DealConfigDraft>(EMPTY_DRAFT);

	const pipelines = useMemo(() => pipelinesData?.pipelines ?? [], [pipelinesData]);

	useEffect(() => {
		if (!dealConfig) {
			return;
		}

		setDraft({
			defaultPipelineId: dealConfig.defaultPipelineId ?? null,
			defaultStageId: dealConfig.defaultStageId ?? null,
			dealNameTemplate: dealConfig.dealNameTemplate ?? "",
			expectedCloseDateOffset: dealConfig.expectedCloseDateOffset ?? 30,
			defaultOwnerId: dealConfig.defaultOwnerId ?? null,
		});
	}, [dealConfig]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(ownersData?.owners ?? []).map((owner) => ({
				id: owner.id,
				title: owner.name ? `${owner.name} (${owner.email})` : owner.email,
			})),
		[ownersData]
	);

	const placeholderOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(sourceFields?.opportunityFields ?? sourceFields?.contactFields ?? []).map((field) => ({
				id: field.name,
				title: `{${field.name}} - ${field.label}`,
			})),
		[sourceFields]
	);

	const handleSave = async () => {
		try {
			// Never persist a stage that belongs to the other id space — the sync would drop it.
			const stageId = resolveSelectableStageId(
				pipelines,
				draft.defaultPipelineId,
				draft.defaultStageId
			);

			await saveDealConfig(buildPayload({ ...draft, defaultStageId: stageId })).unwrap();
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	};

	if (isLoadingConfig) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-text-meta mb-1">{ts("description")}</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<DynamicsPipelinePicker
					pipelineId={draft.defaultPipelineId}
					pipelines={pipelines}
					stageId={draft.defaultStageId}
					ts={ts}
					onChange={({ pipelineId, stageId }) =>
						setDraft((prev) => ({
							...prev,
							defaultPipelineId: pipelineId,
							defaultStageId: stageId,
						}))
					}
				/>

				<div className="md:col-span-2 flex flex-col gap-2">
					<InputField
						handleChange={(event) =>
							setDraft((prev) => ({ ...prev, dealNameTemplate: event.target.value }))
						}
						information={ts("dealNameTemplateHelp")}
						label={ts("dealNameTemplate")}
						name="dealNameTemplate"
						placeholder="{firstName} {lastName} - {company}"
						value={draft.dealNameTemplate}
					/>
					{placeholderOptions.length > 0 && (
						<div className="max-w-[400px]">
							<Dropdown
								data={placeholderOptions}
								floating={true}
								handleSelect={(value) =>
									setDraft((prev) => ({
										...prev,
										dealNameTemplate: `${prev.dealNameTemplate}{${value.id}}`,
									}))
								}
								label={ts("insertPlaceholder")}
							/>
						</div>
					)}
				</div>

				<InputField
					handleChange={(event) =>
						setDraft((prev) => ({
							...prev,
							expectedCloseDateOffset: parseInt(event.target.value, 10) || 0,
						}))
					}
					information={ts("closeDateOffsetHelp")}
					label={ts("closeDateOffsetDays")}
					min={0}
					name="expectedCloseDateOffset"
					type="number"
					value={draft.expectedCloseDateOffset}
				/>

				<Dropdown
					data={ownerOptions}
					defaultValue={ownerOptions.find((option) => option.id === draft.defaultOwnerId)}
					floating={true}
					handleSelect={(value) =>
						setDraft((prev) => ({ ...prev, defaultOwnerId: value.id as string }))
					}
					label={ts("defaultOwner")}
				/>
			</div>

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					testId="dynamics-deal-config-save"
					title={t("basics.save")}
					type="button"
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
